import { initializeApp, type FirebaseOptions, type FirebaseApp } from 'firebase/app';
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';

// Firebase Web SDK の初期化。dev / prod の切替は VITE_* env で行う。
// VITE_FIRESTORE_EMULATOR_HOST が設定されていれば Firestore は Emulator に接続する。
// VITE_FIREBASE_AUTH_EMULATOR_URL  が設定されていれば Auth は Emulator に接続する。
// VITE_FUNCTIONS_EMULATOR_HOST   が設定されていれば Functions は Emulator に接続する。
//
// customer 側 (apps/customer/src/lib/firebase.ts) と中身がほぼ同一だが、
// `@kawai/shared` は functions (Node) からも参照されるため Web SDK 依存は入れない。
// 将来 3 つ目のアプリが出た時点で別 workspace に集約するか再検討。

const FUNCTIONS_REGION = 'asia-northeast1';

const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
if (!projectId) {
  throw new Error(
    'VITE_FIREBASE_PROJECT_ID is required. Copy .env.example to .env.local and fill in.',
  );
}

const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const db: Firestore = getFirestore(app);
export const auth: Auth = getAuth(app);
export const functions: Functions = getFunctions(app, FUNCTIONS_REGION);

const firestoreEmuHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST;
if (firestoreEmuHost) {
  const [host, portStr] = firestoreEmuHost.split(':');
  const port = Number(portStr);
  if (!host || !Number.isFinite(port)) {
    throw new Error(
      `Invalid VITE_FIRESTORE_EMULATOR_HOST: "${firestoreEmuHost}". Expected "host:port".`,
    );
  }
  connectFirestoreEmulator(db, host, port);
}

const authEmuUrl = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL;
if (authEmuUrl) {
  connectAuthEmulator(auth, authEmuUrl, { disableWarnings: true });
}

const functionsEmuHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST;
if (functionsEmuHost) {
  const [host, portStr] = functionsEmuHost.split(':');
  const port = Number(portStr);
  if (!host || !Number.isFinite(port)) {
    throw new Error(
      `Invalid VITE_FUNCTIONS_EMULATOR_HOST: "${functionsEmuHost}". Expected "host:port".`,
    );
  }
  connectFunctionsEmulator(functions, host, port);
}
